import clsx from 'clsx'
import PropTypes from 'prop-types'
import Swal from 'sweetalert2'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import http from '../../../../shared/http'
import { errorText, notifyError } from '../../../../shared/errorMessage'
import { TApi, RECORD_TYPES } from '../../../../shared/const'

import Loading from '../../../../UI/Loading'
import RecordComments from '../../../../UI/RecordComments'
import styles from './style.module.scss'
import { formatDateTime as formatDateTimeInVienna } from '../../../../shared/datetime'

/*
 * Detail- und Bearbeitungsansicht einer einzelnen Vorlage (E-Mail oder intern).
 *
 * Der Inhalt lässt sich zwischen gerenderter Klarsicht und HTML-Quelltext
 * umschalten; standardmäßig wird die gerenderte Fassung mit Beispieldaten
 * gezeigt. Vor dem Bearbeiten und Speichern wird gewarnt, dass die Vorlage
 * sofort scharf geschaltet wird. Platzhalter werden nur über Chips eingesetzt.
 */

// Setzt bekannte {{token}} in einer internen Nachricht mit Beispieldaten ein.
const fillInternal = (body, data) =>
	(body || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (match, token) =>
		data && data[token] != null ? data[token] : match
	)

const formatDateTime = value => formatDateTimeInVienna(value)

/*
 * Ein Beispielwert lesbar gemacht.
 *
 * Nicht jeder Platzhalter ist ein Wort: Die Aufgaben-Erinnerung bekommt eine
 * Liste von Anforderungen. `String(...)` machte daraus
 * "[object Object],[object Object]" — der Datensatz sah kaputt aus, obwohl er
 * es nicht war. Listen und Objekte werden deshalb ausgeschrieben.
 */
const sampleText = value => {
	if (value === null || value === undefined) return '—'
	if (Array.isArray(value)) return value.map(sampleText).join(' · ')
	if (typeof value === 'object') {
		return Object.entries(value)
			.map(([key, inner]) => `${key}: ${sampleText(inner)}`)
			.join(', ')
	}
	return String(value)
}

/*
 * Der Grund aus der Vorlagen-Vorschau, ohne den Serverpfad davor: Was hilft,
 * ist der Dateiname mit Zeile und der Satz dahinter — wo die Datei auf dem
 * Server liegt, hilft niemandem und gehört nicht auf den Bildschirm.
 */
const shortenReason = (text = '') => text.replace(/(?:\/[\w.-]+)+\/([\w.-]+\.ejs)/g, '$1')

export default function TemplateDetail({ channel, templateKey, onBack, onSaved, onOpenRelated }) {
	const { t } = useTranslation()
	const isEmail = channel === 'email'

	const [detail, setDetail] = useState(null)
	const [editing, setEditing] = useState(false)
	const [form, setForm] = useState({ subject: '', body: '', isActive: true })
	const [sampleIndex, setSampleIndex] = useState(0)
	// Feinheiten des Auslösers (triggerDetails) - eingeklappt, weil sie die
	// Kurzfassung sonst erschlagen
	const [showTriggerDetails, setShowTriggerDetails] = useState(false)
	const [viewMode, setViewMode] = useState('rendered') // 'rendered' | 'html'
	const [previewHtml, setPreviewHtml] = useState('')
	const [previewError, setPreviewError] = useState('')
	/*
	 * Beim Rendern einer Vorlage ist der technische Grund ausnahmsweise nützlich:
	 * „unbekannt is not defined" benennt den Platzhalter, der nicht stimmt. Er
	 * steht deshalb klein unter dem verständlichen Satz — nicht an dessen Stelle.
	 */
	const [previewReason, setPreviewReason] = useState('')
	const [saving, setSaving] = useState(false)
	// Hoehe der Vorschau. Ein fester Rahmen wuerde laengere Mails abschneiden und
	// zum Scrollen im Scrollbereich zwingen - die Mail soll aber am Stueck lesbar
	// sein, so wie sie beim Empfaenger ankommt.
	const [frameHeight, setFrameHeight] = useState(360)

	const bodyRef = useRef(null)
	const frameRef = useRef(null)

	const detailUrl = isEmail ? TApi.ADMIN_COMM_EMAIL(templateKey) : TApi.ADMIN_COMM_INTERNAL_ITEM(templateKey)
	const headingSubject = detail ? (isEmail ? detail.subject : detail.title) : ''

	const loadDetail = useCallback(async () => {
		setDetail(null)
		try {
			const { data } = await http.get(detailUrl, { disableToast: true })
			setDetail(data)
			setForm({
				subject: isEmail ? data.subject : data.title,
				body: data.body,
				isActive: data.isActive
			})
			setSampleIndex(0)
			setEditing(false)
			setViewMode('rendered')
		} catch (error) {
			notifyError(error, 'Template could not be loaded')
			onBack()
		}
		// t und onBack bewusst NICHT in den Abhaengigkeiten: useTranslation liefert
		// bei jedem Render eine neue Funktionsreferenz, und onBack kommt als
		// Inline-Funktion herein. Mit beiden in der Liste entstuende die Schleife
		// Render -> Laden -> Render (gleiche Falle wie in den Admin-Listen).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [detailUrl, isEmail])

	useEffect(() => {
		loadDetail()
	}, [loadDetail])

	const sample = detail && detail.sampleData.length ? detail.sampleData[sampleIndex] : null
	// Beim Bearbeiten die noch nicht gespeicherte Fassung zeigen, sonst die geladene.
	const previewBody = editing ? form.body : detail?.body

	const fetchEmailPreview = useCallback(async () => {
		if (!isEmail || !detail) return
		setPreviewError('')
		setPreviewReason('')
		try {
			const { data } = await http.post(
				TApi.ADMIN_COMM_EMAIL_PREVIEW(templateKey),
				{ body: previewBody, data: sample || {} },
				{ disableToast: true }
			)
			setPreviewHtml(data.html)
		} catch (error) {
			setPreviewHtml('')
			setPreviewError(errorText(error, 'Preview could not be rendered'))
			setPreviewReason(shortenReason(error.response?.data?.message || ''))
		}
		// t aus demselben Grund draussen wie oben. Der aufrufende Effekt haelt
		// diese Funktion zwar ohnehin aus seinen Abhaengigkeiten heraus - stuende
		// t hier, waere die Schleife nur eine Zeile entfernt.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEmail, detail, templateKey, previewBody, sample])

	// E-Mail-Vorschau (Server-Render) laden, sobald die gerenderte Ansicht sichtbar
	// ist bzw. sich der Beispieldatensatz ändert. Beim Tippen im HTML-Modus wird
	// NICHT bei jedem Zeichen neu gerendert — erst beim Umschalten auf „Gerendert".
	useEffect(() => {
		if (isEmail && detail && viewMode === 'rendered') fetchEmailPreview()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEmail, detail, sampleIndex, viewMode])

	/**
	 * Misst die tatsaechliche Hoehe der gerenderten Mail und uebernimmt sie.
	 *
	 * Der Rahmen laeuft mit `sandbox="allow-same-origin"` und bewusst OHNE
	 * `allow-scripts` - so laesst sich die Hoehe auslesen, waehrend Skripte im
	 * Vorlagen-HTML nicht ausgefuehrt werden.
	 */
	const measureFrame = useCallback(() => {
		const doc = frameRef.current?.contentDocument
		if (!doc?.body) return
		const height = Math.max(doc.body.scrollHeight, doc.documentElement?.scrollHeight || 0)
		if (height > 0) setFrameHeight(height + 8)
	}, [])

	// Nach dem Laden noch einmal nachmessen: Bilder und Schriften kommen spaeter
	// und aendern die Hoehe, das erste Ergebnis waere sonst zu klein.
	useEffect(() => {
		if (!previewHtml) return
		const timer = setTimeout(measureFrame, 300)
		return () => clearTimeout(timer)
	}, [previewHtml, measureFrame])

	const internalPreview = useMemo(() => {
		if (isEmail) return ''
		return fillInternal(previewBody, sample)
	}, [isEmail, previewBody, sample])

	const placeholderToken = token => (isEmail ? `<%= ${token} %>` : `{{${token}}}`)

	const insertPlaceholder = token => {
		const snippet = placeholderToken(token)
		const el = bodyRef.current
		if (!el) {
			setForm(f => ({ ...f, body: f.body + snippet }))
			return
		}
		const start = el.selectionStart ?? el.value.length
		const end = el.selectionEnd ?? el.value.length
		const next = form.body.slice(0, start) + snippet + form.body.slice(end)
		setForm(f => ({ ...f, body: next }))
		requestAnimationFrame(() => {
			el.focus()
			el.selectionStart = el.selectionEnd = start + snippet.length
		})
	}

	const startEditing = async () => {
		const { isConfirmed } = await Swal.fire({
			title: t('Edit template?'),
			html: t(
				'Changes to this template go live immediately. From the next trigger onward, the edited version is sent to real recipients.'
			),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#0CA789',
			cancelButtonColor: '#d33',
			confirmButtonText: t('Edit now'),
			cancelButtonText: t('Cancel')
		})
		if (!isConfirmed) return
		setForm({ subject: headingSubject, body: detail.body, isActive: detail.isActive })
		setViewMode('html') // zum Bearbeiten den Quelltext zeigen
		setEditing(true)
	}

	const cancelEditing = () => {
		setForm({ subject: headingSubject, body: detail.body, isActive: detail.isActive })
		setEditing(false)
		setViewMode('rendered')
	}

	const save = async () => {
		const { value: summary, isConfirmed } = await Swal.fire({
			title: t('Activate template for real sending?'),
			html: t(
				'On saving, this template is switched live and will be sent to real recipients from the next trigger. Continue?'
			),
			icon: 'warning',
			input: 'text',
			inputLabel: t('Note for the change log (optional)'),
			inputPlaceholder: t('e.g. Wording adjusted'),
			showCancelButton: true,
			confirmButtonColor: '#0CA789',
			cancelButtonColor: '#d33',
			confirmButtonText: t('Save and activate'),
			cancelButtonText: t('Cancel')
		})
		if (!isConfirmed) return

		setSaving(true)
		try {
			// `disableToast`: Der eigene Satz benennt die Vorlage, um die es geht.
			await http.put(
				detailUrl,
				{
					subject: form.subject,
					body: form.body,
					isActive: form.isActive,
					summary: summary || undefined
				},
				{ disableToast: true }
			)
			toast.success(t('Template saved and activated'))
			await loadDetail()
			onSaved()
		} catch (error) {
			notifyError(error, 'Template could not be saved')
		} finally {
			setSaving(false)
		}
	}

	if (!detail) return <Loading />

	const related = isEmail ? detail.relatedInternal : detail.relatedEmail

	const renderedView = isEmail ? (
		previewError ? (
			<div className={styles.previewError}>
				{previewError}
				{previewReason && previewReason !== previewError && (
					<span className={styles.previewReason}>{previewReason}</span>
				)}
			</div>
		) : (
			<iframe
				ref={frameRef}
				title={t('Preview')}
				className={styles.previewFrame}
				sandbox="allow-same-origin"
				srcDoc={previewHtml}
				style={{ height: `${frameHeight}px` }}
				onLoad={measureFrame}
			/>
		)
	) : (
		<div className={styles.previewText}>{internalPreview}</div>
	)

	return (
		<div className={styles.detail}>
			<div className={styles.detailHeader}>
				<button type="button" className={styles.backBtn} onClick={onBack}>
					← {t('Back to overview')}
				</button>
				<span className={detail.isActive ? styles.badgeActive : styles.badgeInactive}>
					{detail.isActive ? t('Active') : t('Inactive')}
				</span>
			</div>

			<h2 className={styles.detailTitle}>{detail.title}</h2>

			<div className={styles.liveNotice}>
				<strong>{t('Live templates')}:</strong>{' '}
				{t('These texts are actually sent to users. Edits take effect immediately.')}
			</div>

			{/*
			 * Links der Inhalt, rechts alles Administrative.
			 *
			 * Vorher stand die Mail neben den Beispieldaten und wurde in einem
			 * niedrigen Rahmen abgeschnitten - man musste im Ausschnitt scrollen,
			 * um sie zu lesen. Jetzt bekommt sie die ganze linke Spalte und ihre
			 * volle Hoehe; Zielgruppe, Ausloeser, Beispieldaten, Bearbeiten und
			 * Aenderungslog stehen gebuendelt rechts daneben.
			 */}
			<div className={styles.columns}>
				<div className={styles.contentCol}>
					<label className={styles.fieldLabel}>{isEmail ? t('Subject') : t('Title')}</label>
					<input
						type="text"
						className={styles.subjectInput}
						value={form.subject}
						disabled={!editing}
						onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
					/>

					<div className={styles.bodyLabelRow}>
						<label className={styles.fieldLabel}>{t('Content')}</label>
						<div className={styles.viewToggle}>
							<button
								type="button"
								className={clsx(styles.viewToggleBtn, viewMode === 'rendered' && styles.viewToggleActive)}
								onClick={() => setViewMode('rendered')}
							>
								{t('Rendered')}
							</button>
							<button
								type="button"
								className={clsx(styles.viewToggleBtn, viewMode === 'html' && styles.viewToggleActive)}
								onClick={() => setViewMode('html')}
							>
								{isEmail ? t('HTML source') : t('Text source')}
							</button>
						</div>
					</div>

					{viewMode === 'rendered' ? (
						<div className={styles.renderedWrap}>
							{renderedView}
							{editing && (
								<p className={styles.renderedHint}>
									{t('Switch to source to edit. The preview uses the sample data on the right.')}
								</p>
							)}
						</div>
					) : (
						<>
							<textarea
								ref={bodyRef}
								className={clsx(styles.bodyInput, isEmail && styles.bodyInputMono)}
								value={form.body}
								disabled={!editing}
								spellCheck={!isEmail}
								onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
							/>
							<div className={styles.placeholders}>
								<span className={styles.placeholdersLabel}>{t('Insert placeholder')}:</span>
								{detail.placeholders.length === 0 ? (
									<span className={styles.targetGroupMuted}>—</span>
								) : (
									detail.placeholders.map(ph => (
										<button
											type="button"
											key={ph.token}
											className={styles.chip}
											disabled={!editing}
											title={ph.label}
											onClick={() => insertPlaceholder(ph.token)}
										>
											{ph.label}
										</button>
									))
								)}
							</div>
						</>
					)}

					{editing && (
						<label className={styles.activeToggle}>
							<input
								type="checkbox"
								checked={form.isActive}
								onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
							/>
							{t('Active (is being sent)')}
						</label>
					)}
				</div>

				<aside className={styles.metaCol}>
					<div className={styles.metaBlock}>
						<span className={styles.infoLabel}>{t('Target group')}</span>
						<div className={styles.targetGroups}>
							{detail.targetGroups.length === 0 ? (
								<span className={styles.targetGroupMuted}>{t('Administration')}</span>
							) : (
								detail.targetGroups.map(g => (
									<span key={g} className={styles.targetGroup}>
										{g}
									</span>
								))
							)}
						</div>
					</div>

					<div className={styles.metaBlock}>
						<span className={styles.infoLabel}>{t('Triggered by')}</span>
						<p className={styles.infoTrigger}>{detail.trigger}</p>
						{detail.triggerDetails?.length > 0 && (
							<>
								<button
									type="button"
									className={styles.moreLink}
									onClick={() => setShowTriggerDetails(open => !open)}
								>
									{showTriggerDetails ? t('Less') : t('Read more')}
								</button>
								{showTriggerDetails && (
									<ul className={styles.triggerDetails}>
										{detail.triggerDetails.map(line => (
											<li key={line}>{line}</li>
										))}
									</ul>
								)}
							</>
						)}
						{related && (
							<p className={styles.relatedLine}>
								{isEmail
									? t('This event also triggers an internal message:')
									: t('This event also triggers an email:')}{' '}
								<button
									type="button"
									className={styles.link}
									onClick={() => onOpenRelated(isEmail ? 'internal' : 'email', related.key)}
								>
									{related.title}
								</button>
							</p>
						)}
					</div>

					{detail.isEdited && (
						<div className={styles.metaBlock}>
							<span className={styles.infoLabel}>{t('Last edited')}</span>
							<p className={styles.infoTrigger}>
								{detail.updatedByName || '—'}
								{detail.updatedAt ? ` · ${formatDateTime(detail.updatedAt)}` : ''}
							</p>
						</div>
					)}

					<div className={styles.actions}>
						{editing ? (
							<>
								<button type="button" className={styles.btnPrimary} disabled={saving} onClick={save}>
									{saving ? t('Saving...') : t('Save and activate')}
								</button>
								<button type="button" className={styles.btnGhost} disabled={saving} onClick={cancelEditing}>
									{t('Cancel')}
								</button>
							</>
						) : (
							<button type="button" className={styles.btnPrimary} onClick={startEditing}>
								{t('Edit')}
							</button>
						)}
					</div>

					<div className={styles.metaBlock}>
						<div className={styles.previewHead}>
							<span className={styles.fieldLabel}>{t('Sample data')}</span>
							{detail.sampleData.length > 1 && (
								<div className={styles.sampleNav}>
									<button
										type="button"
										className={styles.sampleNavBtn}
										onClick={() => setSampleIndex(i => (i - 1 + detail.sampleData.length) % detail.sampleData.length)}
									>
										‹
									</button>
									<span>
										{t('Record')} {sampleIndex + 1}/{detail.sampleData.length}
									</span>
									<button
										type="button"
										className={styles.sampleNavBtn}
										onClick={() => setSampleIndex(i => (i + 1) % detail.sampleData.length)}
									>
										›
									</button>
								</div>
							)}
						</div>

						{sample ? (
							<div className={styles.sampleValues}>
								{Object.entries(sample).map(([k, v]) => (
									<div key={k} className={styles.sampleRow}>
										<span className={styles.sampleKey}>{k}</span>
										<span className={styles.sampleVal}>{sampleText(v)}</span>
									</div>
								))}
							</div>
						) : (
							<p className={styles.targetGroupMuted}>{t('No sample data.')}</p>
						)}
					</div>

					{/*
					 * Kommentare zur Vorlage — dieselbe Beschriftung wie an allen
					 * anderen Datensätzen. Der Änderungsverlauf darunter bleibt,
					 * wie er war: Er hält fest, was geändert wurde, die Kommentare
					 * halten fest, warum.
					 */}
					<div className={styles.changelog}>
						<h3 className={styles.changelogTitle}>{t('Comments')}</h3>
						<RecordComments
							entityType={RECORD_TYPES.TEMPLATE}
							entityId={`${channel.toUpperCase()}:${templateKey}`}
						/>
					</div>

					<div className={styles.changelog}>
						<h3 className={styles.changelogTitle}>{t('Change log')}</h3>
						{detail.changelog.length === 0 ? (
							<p className={styles.changelogEmpty}>{t('No changes yet.')}</p>
						) : (
							<ul className={styles.changelogList}>
								{detail.changelog.map(entry => (
									<li key={entry.id} className={styles.changelogItem}>
										<span className={styles.changelogWhen}>{formatDateTime(entry.changedAt)}</span>
										<span className={styles.changelogWho}>{entry.editorName || '—'}</span>
										<span className={styles.changelogWhat}>{entry.summary}</span>
									</li>
								))}
							</ul>
						)}
					</div>
				</aside>
			</div>
		</div>
	)
}

TemplateDetail.propTypes = {
	channel: PropTypes.string.isRequired,
	templateKey: PropTypes.string.isRequired,
	onBack: PropTypes.func.isRequired,
	onSaved: PropTypes.func.isRequired,
	onOpenRelated: PropTypes.func.isRequired
}
